import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Send } from 'lucide-react';

const PingModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Ping User",
    bookingId,
    guestName,
    defaultMessage = "Your planner just pinged you! Please open the app."
}) => {
    const [message, setMessage] = useState(defaultMessage);

    useEffect(() => {
        if (isOpen) {
            setMessage(defaultMessage);
        }
    }, [isOpen, defaultMessage]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
        }
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[200000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div
                className="fixed inset-0 bg-gray-950/30 backdrop-blur-md transition-all duration-500"
                onClick={onClose}
            />

            <div 
                className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl transform transition-all animate-modal-pop overflow-hidden border border-gray-100 font-sans z-10"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="absolute top-4 right-4 z-20">
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }} 
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all cursor-pointer"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="relative px-8 py-10 flex flex-col items-center">
                    <div className="mb-6 p-4 rounded-2xl bg-teal-50 flex items-center justify-center">
                        <Bell className="w-10 h-10 text-[#067978]" strokeWidth={1.5} />
                    </div>

                    <h3 className="text-xl font-extrabold tracking-tight text-gray-900 mb-1 uppercase">
                        {guestName ? `Ping ${guestName}` : title}
                    </h3>
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-8">Booking ID: {bookingId}</p>

                    <div className="w-full mb-8">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Your Message</label>
                        <textarea
                            className="w-full h-32 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-100 text-sm font-semibold text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-100 transition-all resize-none"
                            placeholder="Enter your message here..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="w-full flex items-center justify-center gap-4">
                        <button
                            type="button"
                            className="flex-1 py-3 px-5 rounded-2xl text-slate-600 font-normal text-sm hover:text-slate-900 hover:bg-slate-100 border border-slate-200 transition-all active:scale-95 cursor-pointer"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="flex-[1.5] py-3 px-5 rounded-2xl text-white bg-[#067978] hover:bg-[#056362] font-normal text-sm shadow-md shadow-teal-900/10 transform transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                            onClick={() => {
                                onConfirm(message);
                                onClose();
                            }}
                        >
                            <span>Send Ping</span>
                            <Send size={14} />
                        </button>
                    </div>
                </div>

                <style>{`
                    @keyframes modal-pop {
                        0% { opacity: 0; transform: scale(0.95) translateY(10px); }
                        100% { opacity: 1; transform: scale(1) translateY(0); }
                    }
                    .animate-modal-pop {
                        animation: modal-pop 0.3s ease-out forwards;
                    }
                `}</style>
            </div>
        </div>,
        document.body
    );
};

export default PingModal;
